import { getGlobalVariables, useBehavior, useTriggerAction } from '@libs/core';
import { TWidgetProps } from '@libs/types';
import React from 'react';
import AppleSignin from 'react-apple-signin-auth';
import { getSystemTheme } from '../../theme/ConsumerThemeProvider';
import { TSignInWithAppleData } from '../../types/data';
import { ConsumerActionTypeEnums } from '../../types/enums';
import { TSignInWithAppleOptions } from '../../types/options';
import { ButtonSocialView } from './ButtonSocialView';

/**
 *
 * The Apple JS SDK is loaded clientId from a global variable that must be set in the .env file
 *   NX_PUBLIC_GLOBAL_appleAuthClientId='com.paysend.web'
 *   NX_PUBLIC_GLOBAL_appleAuthRedirectURL='https://paysend.com/auth/consumer3'
 *
 * If Sign In With Apple is unavailable (SDK missing / not loaded), nothing is rendered.
 */
export const ButtonSignInWithApple = ({ widget }: TWidgetProps<TSignInWithAppleData, TSignInWithAppleOptions>) => {
    const { isHidden, isDisabled } = useBehavior(widget);
    const { triggerActionsByType } = useTriggerAction(widget);
    const { appleAuthClientId, appleAuthRedirectURL = window.location.origin + window.location.pathname } = getGlobalVariables(
        'appleAuthClientId',
        'appleAuthRedirectURL',
    );
    const { nonce = 'nonce', messages, requestedScopes = [] } = widget.data ?? ({} as TSignInWithAppleData);
    const { appearance = 'circle' } = widget.options ?? ({} as TSignInWithAppleOptions);

    return (
        <AppleSignin
            /** Auth options passed to AppleID.auth.init() */
            authOptions={{
                /** Client ID - eg: 'com.example.com' */
                clientId: appleAuthClientId,
                /** Requested scopes, seperated by spaces - eg: 'email name' */
                scope: requestedScopes?.join(' '),
                /** Apple's redirectURI - must be one of the URIs you added to the serviceID - the undocumented trick in apple docs is that you should call auth from a page that is listed as a redirectURI, localhost fails */
                redirectURI: appleAuthRedirectURL,
                /** State string that is returned with the apple response */
                // state: 'state',
                /** Nonce */
                nonce: nonce,
                /** Uses popup auth instead of redirection */
                usePopup: true,
            }} // REQUIRED
            /** General props */
            uiType={getSystemTheme()}
            // className="apple-auth-btn"
            /** Removes default style tag */
            noDefaultStyle={false}
            /** Allows to change the button's children, eg: for changing the button text */
            buttonExtraChildren={messages?.label}
            /** Extra controlling props */
            /** Called upon signin success in case authOptions.usePopup = true -- which means auth is handled client side */
            onSuccess={(response: any) => {
                triggerActionsByType(ConsumerActionTypeEnums.onSuccess, { value: response }).then();
            }}
            /** Called upon signin error */
            onError={(error: any) => {
                triggerActionsByType(ConsumerActionTypeEnums.onError, { value: error }).then();
            }}
            /** Skips loading the apple script if true */
            skipScript={false} // default = undefined
            /** render function - called with all props - can be used to fully customize the UI by rendering your own component  */
            render={(props: any) => (
                <ButtonSocialView
                    widget={widget}
                    isHide={isHidden}
                    isDisabled={isDisabled}
                    label={messages?.label}
                    uid={widget.uid}
                    isCircle={appearance === 'circle'}
                    authProvider="apple"
                    onClick={props.onClick}
                />
            )}
        />
    );
};
